import type { FeeBasis } from '@/entities/drug/api/drug.api';
import { DrugBadges } from '@/entities/drug/components/DrugBadges';
import { useRelatedDrugs } from '@/entities/drug/hooks/useDrugQueries';
import {
  formatFeeRate,
  isLinkableDrugCode,
} from '@/entities/drug/utils/drug.utils';
import { AppOnlyButton } from '@/shared/components/app-guide/AppOnlyButton';
import { EmptyState, ErrorState } from '@/shared/components/StateViews';
import { Badge } from '@/shared/components/ui/badge';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { cn } from '@/shared/lib/utils';
import { EMPTY, formatWon } from '@/shared/utils/format';
import { Info } from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

const BASIS_OPTIONS: { value: FeeBasis; label: string }[] = [
  { value: 'PHARMA', label: '제약사 기준' },
  { value: 'USER', label: '사용자 기준' },
];

interface Row {
  drugCode: string;
  drugName: string;
  pharmaCompanyName: string | null;
  isReferenceDrug: boolean;
  isBioequivalent: boolean;
  isGroup: boolean;
  unitPrice: number | null;
  rate: number | null;
  expected: number | null;
  /** 후보 − 현재 예상액 (서버 값). 미확정이면 null */
  diff: number | null;
  /** 제약사 약품 코드 존재 여부 (개인 거래 승인과 다름) */
  tradable: boolean;
}

const formatDiff = (diff: number) =>
  `${diff > 0 ? '+' : diff < 0 ? '-' : '±'}${Math.abs(diff).toLocaleString('ko-KR')}원`;

/**
 * M-12 동일성분 탭 — GET /related가 현재 약(첫 행)을 포함해 정렬까지 해서 준다.
 * 비교 기준(feeBasis) · 동일제형 군 포함을 바꾸면 서버에서 다시 받는다.
 * 수수료율 · 예상액 · 차액은 모두 선택한 기준의 서버 값이다.
 */
export const RelatedDrugsTab = ({
  drugCode,
  onSelect,
}: {
  drugCode: string;
  onSelect: (drugCode: string) => void;
}) => {
  const [basis, setBasis] = useState<FeeBasis>('PHARMA');
  const [includeGroup, setIncludeGroup] = useState(false);
  const { data, isPending, isError, isFetching, refetch } = useRelatedDrugs(
    drugCode,
    basis,
    includeGroup,
  );

  const rows = useMemo(
    () =>
      (data ?? []).map((r) => ({
        current: r.current,
        row: {
          drugCode: r.drug.drugCode,
          drugName: r.drug.drugName,
          pharmaCompanyName: r.drug.pharmaCompanyName,
          isReferenceDrug: r.drug.referenceDrug,
          isBioequivalent: r.drug.bioequivalent,
          isGroup: r.sameFormulationGroup,
          unitPrice: r.drug.price,
          rate: r.feeRate,
          expected: r.estimatedAmount,
          diff: r.differenceAmount,
          tradable: r.drug.tradable,
        } satisfies Row,
      })),
    [data],
  );
  const currentRow = rows.find((r) => r.current)?.row;
  const candidates = rows.filter((r) => !r.current).map((r) => r.row);

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap items-center gap-2.5">
        <div
          role="radiogroup"
          aria-label="수수료율 기준"
          className="flex rounded-[9px] bg-[#f2f2f2] p-[3px]"
        >
          {BASIS_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              role="radio"
              aria-checked={basis === o.value}
              onClick={() => setBasis(o.value)}
              className={cn(
                'rounded-[7px] px-3.5 py-[7px] text-[12.5px] leading-[19px] font-medium',
                basis === o.value ? 'bg-ink text-white' : 'text-ink-3',
              )}
            >
              {o.label}
            </button>
          ))}
        </div>
        <label className="flex cursor-pointer items-center gap-2 text-[12.5px] leading-[19px] text-ink-2">
          <Checkbox
            checked={includeGroup}
            onCheckedChange={(v) => setIncludeGroup(v === true)}
            className="size-4 rounded-[4px] border-[#c9c9c9]"
          />
          동일제형 군 포함
          <span title="성분이 같고 제형 군이 같은 품목까지 함께 보여줍니다">
            <Info className="size-3 text-[#a8a8a8]" aria-hidden />
          </span>
        </label>
      </div>

      <div className="mt-3.5 flex items-center gap-2">
        <span className="text-[13px] leading-5 font-bold text-ink">
          동일성분 의약품
        </span>
        {/* 건수는 현재 약을 포함한 배열 길이 */}
        <Badge tone="primary">{isPending ? '-' : rows.length}건</Badge>
        <span className="ml-auto text-[11px] leading-[17px] text-ink-6">
          예상 금액 높은순
        </span>
      </div>

      <div
        className={cn(
          'mt-3 overflow-x-auto transition-opacity',
          isFetching && !isPending && 'opacity-60',
        )}
      >
        <ul className="flex min-w-[540px] flex-col">
          {isPending ? (
            <li className="flex flex-col gap-2 pt-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </li>
          ) : isError ? (
            <li>
              <ErrorState onRetry={() => void refetch()} />
            </li>
          ) : (
            <>
              {currentRow && <RelatedRow row={currentRow} isCurrent />}
              {candidates.length === 0 ? (
                <li>
                  <EmptyState message="동일성분 의약품이 없어요." />
                </li>
              ) : (
                candidates.map((r) => (
                  <Fragment key={r.drugCode}>
                    <li aria-hidden className="h-px bg-[#f3f3f3]" />
                    <RelatedRow
                      row={r}
                      // 의약품 정보가 없는 품목(코드 "-" · ru…)은 상세를 열 수 없다
                      onSelect={
                        isLinkableDrugCode(r.drugCode)
                          ? () => onSelect(r.drugCode)
                          : undefined
                      }
                    />
                  </Fragment>
                ))
              )}
            </>
          )}
        </ul>
      </div>
    </div>
  );
};

const RelatedRow = ({
  row,
  isCurrent = false,
  onSelect,
}: {
  row: Row;
  isCurrent?: boolean;
  onSelect?: () => void;
}) => {
  const diff = isCurrent ? null : row.diff;

  return (
    <li
      onClick={onSelect}
      onKeyDown={(e) => {
        if (onSelect && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault();
          onSelect();
        }
      }}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      className={cn(
        'flex items-center gap-2.5 rounded-md px-2.5 py-[11px]',
        isCurrent
          ? 'mb-2 bg-[#f7f9ff]'
          : onSelect
            ? 'my-1 cursor-pointer hover:bg-subtle'
            : 'my-1',
      )}
    >
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <span
          className={cn(
            'text-[12.5px] leading-[19px] text-ink',
            isCurrent ? 'font-bold' : 'font-medium',
          )}
        >
          {row.drugName}
        </span>
        <span className="flex flex-wrap items-center gap-[5px]">
          <span className="text-[10.5px] leading-4 text-ink-7">
            {row.pharmaCompanyName}
          </span>
          {isCurrent && (
            <Badge className="bg-ink px-[7px] text-[10px] leading-[15px] text-white">
              현재
            </Badge>
          )}
          <DrugBadges
            small
            isReferenceDrug={row.isReferenceDrug}
            isBioequivalent={row.isBioequivalent}
          />
          {row.isGroup && (
            <Badge
              tone="neutral"
              className="px-[7px] text-[10px] leading-[15px]"
            >
              동일제형 군
            </Badge>
          )}
        </span>
      </div>
      <span className="w-16 shrink-0 text-right text-xs leading-[18px] text-ink-3">
        {formatWon(row.unitPrice)}
      </span>
      <span
        className={cn(
          'w-[52px] shrink-0 text-right text-xs leading-[18px] font-medium',
          row.rate == null ? 'text-[#c4c4c4]' : 'text-primary',
        )}
      >
        {row.rate == null ? EMPTY : formatFeeRate(row.rate)}
      </span>
      <div className="flex w-[104px] shrink-0 flex-col items-end gap-0.5 text-right">
        <span
          className={cn(
            'text-[13px] leading-5 font-bold',
            row.expected == null ? 'text-[#c4c4c4]' : 'text-success',
          )}
        >
          {row.expected == null ? EMPTY : formatWon(row.expected)}
        </span>
        <span
          className={cn(
            'text-[10px] leading-[15px]',
            isCurrent
              ? 'text-ink-7'
              : !row.tradable
                ? 'text-warning'
                : diff != null && diff > 0
                  ? 'text-success'
                  : diff != null && diff < 0
                    ? 'text-warning'
                    : 'text-ink-7',
          )}
        >
          {isCurrent
            ? '기준'
            : !row.tradable
              ? '거래 불가'
              : diff != null
                ? formatDiff(diff)
                : EMPTY}
        </span>
      </div>
      <div
        className="flex w-24 shrink-0 justify-end"
        // 신청 버튼을 눌러도 행 클릭(내용 교체)이 일어나지 않게 막는다
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {!isCurrent && row.tradable ? (
          <AppOnlyButton
            feature="filteringApply"
            className="h-auto gap-[5px] rounded-sm border-0 bg-[#f4f4f4] px-2.5 py-[7px] text-[11px] leading-[17px] text-[#8d8d8d]"
          >
            필터링 신청
          </AppOnlyButton>
        ) : (
          <span className="text-xs leading-[18px] text-[#c4c4c4]">{EMPTY}</span>
        )}
      </div>
    </li>
  );
};
